import { ErrorMessage, type FormikErrors } from "formik";
import { Component } from "react";

import { getValidateStatusTouched } from "utilities/form/form";
import yup from "utilities/form/yup";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { btDeleteConfirm, btInfo } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { ColorPicker } from "commonComponents/entity/colorPicker/ColorPicker/ColorPicker";
import { getUnsavedChangesModalConfig } from "commonComponents/utilities/DirtyTracking/UnsavedChangesPrompt";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import {
    type ICategoryDetails,
    type ILeadActivityCategoryDetails,
    isLeadActivityCategoryDetails,
} from "entity/category/Category/Category.api.types";
import { type LeadActivityIcon } from "entity/lead/Common/leadActivityIcon";
import { LeadActivityIconDisplay } from "entity/lead/Common/LeadActivityIconDisplay/LeadActivityIconDisplay";
import { LeadActivityIconPicker } from "entity/lead/Common/LeadActivityIconPicker/LeadActivityIconPicker";

import "./CategoryPresentational.less";

import { CategoryType } from "../common/categoryType";

const getMaxTitleLength = (categoryType: CategoryType) =>
    categoryType === CategoryType.JobGroups ? 25 : 50;

interface ICategoryPresentationalProps {
    categoryDetail: ICategoryDetails | ILeadActivityCategoryDetails;
    categoryType: CategoryType;
    actionBeingPerformed: CategoryFormActions;
    onSubmit: (
        values: CategoryFormValues,
        setErrors: (errors: FormikErrors<CategoryFormValues>) => void,
    ) => Promise<boolean>;
    onDelete: (id: number) => Promise<void>;
    title: string;
    modalConfig?: IModalConfiguration;
}

export class CategoryFormValues {
    title: string;
    categoryColor?: string;
    categoryIcon?: LeadActivityIcon;
}

export type CategoryFormActions = undefined | "save" | "delete";

class CategoryPresentational extends Component<
    IBTInjectedFormikProps<ICategoryPresentationalProps, CategoryFormValues>
> {
    private get categoryFriendlyNameForDisplay() {
        return this.props.categoryDetail.categoryFriendlyName;
    }
    private onDelete = async () => {
        const { categoryDetail, setSubmitting, onDelete } = this.props;

        if (categoryDetail.isAssociatedWithEntity) {
            // can not delete
            btInfo({
                title: `Unable to delete this ${this.categoryFriendlyNameForDisplay.toLocaleLowerCase()}`,
                content: categoryDetail.onDeleteMessage,
            });
        } else {
            btDeleteConfirm({
                entityName: this.categoryFriendlyNameForDisplay.toLocaleLowerCase(),
                content: categoryDetail.onDeleteMessage,
                onOk: async () => {
                    setSubmitting(true);
                    try {
                        await onDelete(categoryDetail.id!);
                    } finally {
                        setSubmitting(false);
                    }
                },
            });
        }
    };

    private handleSaveAndClose = async () => {
        const {
            setSubmitting,
            values,
            setErrors,
            validateForm,
            handleSubmit,
            onSubmit,
            modalConfig,
        } = this.props;
        setSubmitting(true);
        const formErrors = await validateForm();
        if (Object.keys(formErrors).length === 0) {
            const success = await onSubmit(values, setErrors);
            if (success) {
                modalConfig?.beforeClose();
            }
        } else {
            // show error summary
            handleSubmit();
        }
        setSubmitting(false);
    };

    private getModalConfig = () => {
        const { getDirtyTrackingProps, modalConfig } = this.props;
        return getUnsavedChangesModalConfig(
            getDirtyTrackingProps,
            {
                onCloseWithSave: this.handleSaveAndClose,
                onCloseWithoutSave: modalConfig?.beforeClose,
            },
            modalConfig?.parentRoute,
        );
    };

    render() {
        const {
            categoryDetail,
            values,
            touched,
            errors,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            handleSubmit,
            actionBeingPerformed,
            title,
        } = this.props;
        const isNew = !categoryDetail.id;

        return (
            <BTModalLayout
                title={title}
                footerContent={
                    <>
                        {categoryDetail.canEdit && (
                            <BTButton
                                type="primary"
                                onClick={() => handleSubmit()}
                                loading={isSubmitting}
                                data-testid="catSave"
                                actionBeingPerformed={actionBeingPerformed}
                                loadingAction="save"
                                hotkey="save"
                            >
                                Save
                            </BTButton>
                        )}
                        {categoryDetail.canDelete && !isNew && (
                            <BTButton
                                loading={isSubmitting}
                                onClick={this.onDelete}
                                data-testid="catDelete"
                                actionBeingPerformed={actionBeingPerformed}
                                loadingAction="delete"
                                hotkey="delete"
                                type="secondary"
                            >
                                Delete
                            </BTButton>
                        )}
                    </>
                }
                modalConfig={this.getModalConfig()}
            >
                <BTForm>
                    <BTLayoutContent>
                        <PageSection
                            title={`${isNew ? "Add" : "Edit"} ${
                                this.categoryFriendlyNameForDisplay
                            }`}
                        >
                            <BTFormItem
                                required
                                label="Title"
                                validateStatus={getValidateStatusTouched(errors, touched, "title")}
                                help={<ErrorMessage name="title" />}
                            >
                                <BTInput
                                    id="title"
                                    data-testid="catTitle"
                                    autoFocus={isNew}
                                    value={values.title}
                                    onChange={setFieldValue}
                                    onBlur={setFieldTouched}
                                />
                            </BTFormItem>
                            {isLeadActivityCategoryDetails(categoryDetail) && (
                                <BTRow responsiveMode="viewport">
                                    <BTCol xs={24} md={16}>
                                        <BTTitle level={4}>
                                            <b>Set up a color and icon for the activity type:</b>
                                        </BTTitle>
                                        <BTFormItem
                                            label="Color"
                                            validateStatus={getValidateStatusTouched(
                                                errors,
                                                touched,
                                                "categoryColor",
                                            )}
                                            help={<ErrorMessage name="categoryColor" />}
                                        >
                                            <ColorPicker<CategoryFormValues>
                                                id="categoryColor"
                                                data-testid="categoryColor"
                                                onBlur={setFieldTouched}
                                                onChange={setFieldValue}
                                                value={values.categoryColor}
                                            />
                                        </BTFormItem>
                                        <BTFormItem
                                            label="Icon"
                                            validateStatus={getValidateStatusTouched(
                                                errors,
                                                touched,
                                                "categoryIcon",
                                            )}
                                            help={<ErrorMessage name="categoryIcon" />}
                                        >
                                            <LeadActivityIconPicker<CategoryFormValues>
                                                id="categoryIcon"
                                                data-testid="categoryIcon"
                                                onBlur={setFieldTouched}
                                                onChange={setFieldValue}
                                                value={values.categoryIcon}
                                                treeData={categoryDetail.leadActivityIcons}
                                            />
                                        </BTFormItem>
                                    </BTCol>
                                    <BTCol xs={24} md={8}>
                                        <div className="CategoryPresentational-Icon-Preview-Container">
                                            <BTTitle
                                                level={4}
                                                className="CategoryPresentational-Icon-Preview-Text"
                                            >
                                                <b>Preview:</b>
                                            </BTTitle>
                                            <LeadActivityIconDisplay
                                                icon={values.categoryIcon}
                                                color={values.categoryColor}
                                                size={4}
                                            />
                                        </div>
                                    </BTCol>
                                </BTRow>
                            )}
                        </PageSection>
                    </BTLayoutContent>
                </BTForm>
            </BTModalLayout>
        );
    }
}

const CategoryPresentationalValidators = (props: ICategoryPresentationalProps) =>
    yup.object().shape<CategoryFormValues>({
        title: yup
            .string()
            .required()
            .label(`${props.categoryDetail.categoryFriendlyName} Name`)
            .max(getMaxTitleLength(props.categoryType)),
    });

export const CategoryPresentationalForm = withFormikBT<
    ICategoryPresentationalProps,
    CategoryFormValues
>({
    // use the default values we specified above
    mapPropsToValues: (props: ICategoryPresentationalProps): CategoryFormValues => {
        const { categoryDetail } = props;
        let categoryIcon: LeadActivityIcon | undefined;
        let categoryColor: string | undefined;

        if (isLeadActivityCategoryDetails(categoryDetail)) {
            categoryColor = categoryDetail.leadActivityColor;
            const selectedIcon = categoryDetail.leadActivityIcons.find((i) => i.selected);
            categoryIcon = selectedIcon && selectedIcon.value;
        }

        return {
            title: categoryDetail.categoryTitle,
            categoryColor,
            categoryIcon,
        };
    },

    // use the yup validation object we specified above
    validationSchema: CategoryPresentationalValidators,
    validateOnChange: true,
    validateOnBlur: true,

    // use the submit function we specified above
    handleSubmit: async (values: CategoryFormValues, { props, setErrors, setSubmitting }) => {
        setSubmitting(true);
        try {
            await props.onSubmit(values, setErrors);
        } finally {
            setSubmitting(false);
        }
    },
})(CategoryPresentational);
